import React, { useRef, useState, useEffect } from "react";
import {
  DmsPage, intl, HTTP_STATUS, DmsTable, DmsTableDeleteButton, DmsButtonGroup, DmsFlexContainer, fetchData, DmsButton, DmsSwitch
} from "dmsbasereact";
import Swal from "sweetalert2";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import { Select, MenuItem, InputLabel, FormControl, Typography, Chip, Grid } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import InfoIcon from "@mui/icons-material/Info";

export const ApiKeyManagement = () => {
  const table = useRef(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUserPk, setSelectedUserPk] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedApiKeyDetails, setSelectedApiKeyDetails] = useState(null);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetchData("/user/all", "GET", true);
        if (response.status === HTTP_STATUS.OK_200) {
          setUsers(response.data.data || []);
        } else {
          console.error("Failed to fetch users:", response?.data?.detail);
          setUsers([]);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
        setUsers([]);
      }
    };

    if (createModalOpen) {
      fetchUsers();
    }
  }, [createModalOpen]);

  const handleCreateApiKey = async () => {
    if (!selectedUserPk) {
      Swal.fire({
        icon: "warning",
        title: intl.get("PleaseSelectUser").d("Please select a user"),
      });
      return;
    }

    try {
      const requestBody = {
        user_pk: selectedUserPk
      };

      const response = await fetchData("/api_key/manage", "POST", true, requestBody, false, true);
      if (response.status === HTTP_STATUS.CREATED_201) {
        Swal.fire({
          icon: "success",
          title: intl.get("PermissionGranted").d("Permission granted successfully"),
          text: intl.get("UserCanNowGenerate").d("The user can now generate their API key."),
        });
        table.current.updateTable();
        setCreateModalOpen(false);
        setSelectedUserPk(null);
      } else {
        Swal.fire({
          icon: "error",
          title: response?.data?.detail || intl.get("FailedToCreateApiKeyPermission").d("Failed to create API key permission"),
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: intl.get("FailedToCreateApiKeyPermission").d("Failed to create API key permission"),
        text: error?.response?.data?.detail || error.message,
      });
    }
  };

  const handleToggleApiKeyStatus = async (pk, currentStatus) => {
    const newStatus = !currentStatus;
    const actionText = newStatus ? intl.get("activate").d("activate") : intl.get("deactivate").d("deactivate");

    Swal.fire({
      title: intl.get("ConfirmAndJustifyApiKeyStatusChange").d("Confirm & Justify API Key Status Change"),
      html: `
        <p>${intl.get("ApiKeyManagement.ConfirmToggleMessage", { actionText }).d("Are you sure you want to {actionText} this API key?")}</p>
        <input id="swal-input-justification" class="swal2-input" placeholder="${intl.get("EnterJustificationHere").d("Enter justification here...")}">
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: intl.get("Yes").d("Yes"),
      cancelButtonText: intl.get("Cancel").d("Cancel"),
      preConfirm: () => {
        const justification = document.getElementById("swal-input-justification").value;
        if (!justification) {
          Swal.showValidationMessage(intl.get("JustificationRequired").d("Justification is required!"));
          return false;
        }
        return justification;
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        const justification = result.value;
        try {
          const requestBody = {
            is_active: newStatus,
            justification: justification,
          };
          const response = await fetchData(`/api_key/${pk}/toggle_status`, "PUT", true, requestBody, false, true);
          if (response.status === HTTP_STATUS.OK_200) {
            Swal.fire({
              icon: "success",
              title: intl.get("ApiKeyStatusUpdatedSuccessfully").d("API Key status updated successfully"),
            });
            table.current.updateTable();
          } else {
            Swal.fire({
              icon: "error",
              title: response?.data?.detail || intl.get("FailedToUpdateApiKeyStatus").d("Failed to update API key status"),
            });
          }
        } catch (error) {
          Swal.fire({
            icon: "error",
            title: intl.get("FailedToUpdateApiKeyStatus").d("Failed to update API key status"),
          });
        }
      } else if (result.dismiss === Swal.DismissReason.cancel) {
        Swal.fire({
          icon: "info",
          title: intl.get("StatusChangeCancelled").d("Status change cancelled"),
          text: intl.get("ActionCancelledByUser").d("The action was cancelled by the user."),
        });
      }
    });
  };

  const handleViewDetails = (row) => {
    setSelectedApiKeyDetails(row);
    setDetailModalOpen(true);
  };

  const header = [
    { label: intl.get("Responsible").d("Responsible"), column_name: "user_name", filter: true },
    {
      label: intl.get("KeyStatus").d("Key Status"),
      column_name: "key_display",
      width: "150px",
      sortable: false,
      element: (row) => (
        <Chip
          label={row.key_display || intl.get("NotGenerated").d("Not generated")}
          color={row.has_key ? "success" : "warning"}
          size="small"
        />
      ),
    },
    {
      label: intl.get("IsActive").d("Is Active"),
      column_name: "is_active",
      width: "100px",
      sortable: false,
      element: (row) => (
        <DmsSwitch
          name={`api_key_status_${row.pk}`}
          checked={row.is_active}
          onChange={(e) => handleToggleApiKeyStatus(row.pk, row.is_active)}
          color={row.is_active ? "primary" : "default"}
        />
      ),
    },
    {
      label: intl.get("Actions").d("Actions"),
      column_name: "actions",
      width: "120px",
      sortable: false,
      element: (row) => (
        <DmsButtonGroup>
          <DmsButton
            icon={<InfoIcon />}
            title={intl.get("ViewDetails").d("View Details")}
            onClick={() => handleViewDetails(row)}
          />
          <DmsTableDeleteButton
            table={table}
            title={intl.get("DeleteApiKey").d("Delete API Key")}
            text={intl.get("ApiKeyManagement.DeleteButtonMessage").d(`This API Key permission will be deleted. Are you sure?`)}
            url="/api_key/"
            row_pk={row.pk}
          />
        </DmsButtonGroup>
      ),
    },
  ];

  const toolbar = (
    <DmsFlexContainer>
      <DmsFlexContainer.Left>
        <DmsButton
          variant="primary"
          icon={<AddIcon />}
          onClick={() => setCreateModalOpen(true)}
        />
      </DmsFlexContainer.Left>
    </DmsFlexContainer>
  );

  const createModal = (
    <Modal
      open={createModalOpen}
      onClose={() => {
        setCreateModalOpen(false);
        setSelectedUserPk(null);
      }}
      aria-labelledby="create-api-key-modal-title"
      aria-describedby="create-api-key-modal-description"
    >
      <Box sx={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: 450,
        bgcolor: "background.paper",
        border: "2px solid #000",
        boxShadow: 24,
        p: 4,
      }}>
        <Typography id="create-api-key-modal-title" variant="h6" component="h2">
          {intl.get("GrantApiKeyPermission").d("Grant API Key Permission")}
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, mb: 2, color: "text.secondary" }}>
          {intl.get("GrantPermissionDescription").d("Grant permission for a user to generate their own API key. The user will generate the actual key themselves.")}
        </Typography>
        <Box sx={{ mt: 2 }}>
          <FormControl fullWidth required>
            <InputLabel id="user-select-label">{intl.get("SelectUser").d("Select User")}</InputLabel>
            <Select
              labelId="user-select-label"
              id="user_select"
              value={selectedUserPk ?? ""}
              label={intl.get("SelectUser").d("Select User")}
              onChange={(event) => setSelectedUserPk(event.target.value ? parseInt(event.target.value) : null)}
            >
              <MenuItem value="">
                <em>{intl.get("SelectAUser").d("-- Select a user --")}</em>
              </MenuItem>
              {users.map((user) => (
                <MenuItem key={user.pk} value={user.pk}>
                  {user.user_name} ({user.user_login})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <DmsButton
            variant="secondary"
            text={intl.get("Cancel").d("Cancel")}
            onClick={() => {
              setCreateModalOpen(false);
              setSelectedUserPk(null);
            }}
          />
          <DmsButton
            variant="primary"
            onClick={handleCreateApiKey}
            text={intl.get("GrantPermission").d("Grant Permission")}
          />
        </Box>
      </Box>
    </Modal>
  );

  const detailModal = (
    <Modal
      open={detailModalOpen}
      onClose={() => setDetailModalOpen(false)}
      aria-labelledby="api-key-detail-modal-title"
      aria-describedby="api-key-detail-modal-description"
    >
      <Box sx={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: 500,
        bgcolor: "background.paper",
        border: "2px solid #000",
        boxShadow: 24,
        p: 4,
      }}>
        <Typography id="api-key-detail-modal-title" variant="h6" component="h2" sx={{ mb: 2 }}>
          {intl.get("ApiKeyDetails").d("API Key Details")}
        </Typography>
        {selectedApiKeyDetails && (
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12}>
              <Typography variant="body1"><strong>{intl.get("UserName").d("User Name")}:</strong> {selectedApiKeyDetails.user_name || "-"}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body1">
                <strong>{intl.get("KeyStatus").d("Key Status")}:</strong>{" "}
                <Chip
                  label={selectedApiKeyDetails.key_display || intl.get("NotGenerated").d("Not generated")}
                  color={selectedApiKeyDetails.has_key ? "success" : "warning"}
                  size="small"
                />
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body1"><strong>{intl.get("IsActive").d("Is Active")}:</strong> {selectedApiKeyDetails.is_active ? intl.get("Yes").d("Yes") : intl.get("No").d("No")}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body1"><strong>{intl.get("Justification").d("Justification")}:</strong> {selectedApiKeyDetails.justification || "-"}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body1"><strong>{intl.get("CreatedAt").d("Created At")}:</strong> {selectedApiKeyDetails.created_on || "-"}</Typography>
            </Grid>
          </Grid>
        )}
        <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
          <DmsButton
            variant="secondary"
            text={intl.get("Close").d("Close")}
            onClick={() => setDetailModalOpen(false)}
          />
        </Box>
      </Box>
    </Modal>
  );

  return (
    <DmsPage name={intl.get("ApiKeyManagement").d("API Key Management")}>
      <DmsTable
        header={header}
        managed={true}
        needs_authentication={true}
        export_table={false}
        ref={table}
        url={'/api_key/table'}
        toolbar={toolbar}
      />
      {createModal}
      {detailModal}
    </DmsPage>
  );
};
